import { useQuery } from '@tanstack/react-query';
import { CandidateService } from '../CandidateService';

// Ranked requirement matches for the submit-candidates screen.
// tier / search / page are part of the queryKey so changing any of them
// refetches; staleTime 0 keeps the ranked list fresh like the old manual fetch.
export const useRequirementMatches = ({
  opportunityId,
  page = 1,
  pageSize = 10,
  search = '',
  tier = 'all',
  enabled = true,
}) =>
  useQuery({
    queryKey: [
      'requirement-matches',
      opportunityId,
      page,
      pageSize,
      search,
      tier,
    ],
    queryFn: () =>
      CandidateService.getRequirementMatches(opportunityId, {
        page,
        pageSize,
        search,
        tier: tier && tier !== 'all' ? tier : null,
      }),
    enabled: enabled && !!opportunityId,
    staleTime: 0,
    placeholderData: (prev) => prev,
  });

// Enforcement modes for each constraint on a requirement. Only the applicable
// constraints are shown in the Match Rules section; Off (remove) is always
// included alongside the returned allowed_modes.
export const useRequirementConstraintModes = (
  requirementId,
  { enabled = true } = {}
) =>
  useQuery({
    queryKey: ['requirement', 'constraint-modes', requirementId],
    queryFn: () =>
      CandidateService.getRequirementConstraintModes(requirementId),
    enabled: enabled && !!requirementId,
    staleTime: 0,
  });

// Candidate Status queries
export const useCandidatesByStatus = ({
  opportunityId,
  status,
  page,
  pageSize,
  requirementLocationId,
  enabled = true,
}) =>
  useQuery({
    queryKey: [
      'candidates',
      'by-status',
      opportunityId,
      status,
      page,
      pageSize,
      requirementLocationId,
    ],
    queryFn: () =>
      CandidateService.getCandidatesByStatus({
        opportunityId,
        status,
        page,
        pageSize,
        requirementLocationId,
      }),
    enabled: enabled && !!opportunityId && !!requirementLocationId,
    staleTime: 0, // Data is immediately stale - always refetch
    gcTime: 0, // Don't cache - remove from cache immediately after unmount
    refetchOnMount: true, // Always refetch on mount
  });

export const useCandidatesByStatusForTa = ({
  taLeadAssignmentId,
  status,
  page,
  pageSize,
  requirementLocationId,
  search,
  enabled = true,
}) =>
  useQuery({
    queryKey: [
      'candidates',
      'by-status-ta',
      taLeadAssignmentId,
      status,
      page,
      pageSize,
      requirementLocationId,
      search,
    ],
    queryFn: () =>
      CandidateService.getCandidatesByStatusForTa({
        assignmentId: taLeadAssignmentId,
        requirementLocationId,
        status,
        page,
        pageSize,
        search,
      }),
    enabled: enabled && !!taLeadAssignmentId && !!requirementLocationId,
    staleTime: 0, // Data is immediately stale - always refetch
    gcTime: 0, // Don't cache - remove from cache immediately after unmount
    refetchOnMount: true, // Always refetch on mount
  });

// "Today at a glance" tiles' real data source — see PipelineOverviewHeader.jsx.
export const useCandidatesPipelineSummary = ({
  assignmentId,
  requirementLocationId,
  enabled = true,
}) =>
  useQuery({
    queryKey: [
      'candidates',
      'pipeline-summary',
      assignmentId,
      requirementLocationId,
    ],
    queryFn: () =>
      CandidateService.getCandidatesPipelineSummary({
        assignmentId,
        requirementLocationId,
      }),
    enabled: enabled && !!assignmentId && !!requirementLocationId,
  });

export const useExternalCandidates = ({
  taLeadAssignmentId,
  page,
  pageSize,
  // DIRECT | INTERNAL | PARTNER — the real candidate_source enum, sent in
  // full caps (confirmed, same values CandidateCell.jsx's own
  // CANDIDATE_SOURCE_CFG uses for the source badge elsewhere).
  source = 'DIRECT',
  enabled = true,
}) =>
  useQuery({
    queryKey: [
      'candidates',
      'external',
      taLeadAssignmentId,
      page,
      pageSize,
      source,
    ],
    queryFn: () =>
      CandidateService.getExternalCandidates({
        assignmentId: taLeadAssignmentId,
        page,
        pageSize,
        source,
      }),
    enabled: enabled && !!taLeadAssignmentId,
    staleTime: 0, // Data is immediately stale - always refetch
    gcTime: 0, // Don't cache - remove from cache immediately after unmount
    refetchOnMount: true, // Always refetch on mount
  });

// Candidate Profile queries. `enabled` lets callers gate the fetch (e.g. only
// while a modal is open); opportunityId is optional for the profile lookup.
export const useCandidateProfile = (
  id,
  opportunityId,
  { enabled = true } = {}
) =>
  useQuery({
    queryKey: ['candidate', 'profile', id, opportunityId],
    queryFn: () => CandidateService.getCandidateProfile(id, opportunityId),
    enabled: !!id && enabled,
  });

// Filter config (event categories / actor types) for the candidate
// profile's Audit Log tab — only fetched once that tab is active.
export const useAuditConfig = ({ enabled = true } = {}) =>
  useQuery({
    queryKey: ['candidate', 'audit-config'],
    queryFn: () => CandidateService.getAuditConfig(),
    enabled,
  });

// Paginated audit trail for a single candidate — page/search/filter/sort
// are all part of the queryKey so changing any of them refetches.
export const useCandidateAuditLogs = ({
  candidateId,
  page = 1,
  pageSize = 10,
  search = '',
  eventCategory = '',
  actorType = '',
  sortBy = 'newest',
  enabled = true,
}) =>
  useQuery({
    queryKey: [
      'candidate',
      'audit-logs',
      candidateId,
      page,
      pageSize,
      search,
      eventCategory,
      actorType,
      sortBy,
    ],
    queryFn: () =>
      CandidateService.getCandidateAuditLogs({
        candidateId,
        page,
        pageSize,
        search,
        eventCategory,
        actorType,
        sortBy,
      }),
    enabled: enabled && !!candidateId,
    placeholderData: (prev) => prev,
  });

// Lookup a candidate by phone or email (either one is sufficient).
export const useCandidateByContact = ({ phone, email, enabled = true }) =>
  useQuery({
    queryKey: ['candidate', 'by-contact', phone || null, email || null],
    queryFn: () => CandidateService.getCandidateByContact({ phone, email }),
    enabled: enabled && (!!phone || !!email),
    gcTime: 0,
  });

export const useCandidateScores = (candidateId, opportunityId) =>
  useQuery({
    queryKey: ['candidate', 'scores', candidateId, opportunityId],
    queryFn: () =>
      CandidateService.getCandidateScores(candidateId, opportunityId),
    enabled: !!candidateId && !!opportunityId,
  });

// Skill-match prefill for the Skills tab (UpdateProfileDetailsModal) — the
// requirement's required skills plus the candidate's own extra core skills,
// each with whatever proficiency/experience/recency is already known.
// `secMappingId` is only meaningful for one candidate at a time, so the
// caller gates `enabled` on exactly one being selected (a bulk capture has
// no single candidate to match against).
export const useCandidateSkillMatch = (secMappingId, { enabled = true } = {}) =>
  useQuery({
    queryKey: ['candidate', 'skill-match', secMappingId],
    queryFn: () => CandidateService.getCandidateSkillMatch(secMappingId),
    enabled: enabled && !!secMappingId,
  });

// Prefill for the reschedule_slot modal (ActionPayloadModal, senior_level_
// flow) — the candidate's existing interview slot. Keyed by secMappingId,
// same as useCandidateSkillMatch above (an earlier version of this endpoint
// keyed on assignmentId + the real candidate_id instead — since dropped).
//
// refetchOnMount: 'always' is a defensive default, NOT the actual fix for
// "doesn't refetch on reopen" (reported live, confirmed by testing this
// exact change: still exactly one network call across two opens). The real
// fix is ActionPayloadModal's own explicit refetch() call — this modal
// never unmounts (GenericFlowAction renders it unconditionally, only
// `open` toggles), so `enabled` flipping false→true on an already-mounted
// useQuery does not count as a fresh "mount" to React Query, and
// refetchOnMount never fires. Left here anyway in case this hook is ever
// used somewhere with real mount/unmount semantics, where it would matter.
export const useInterviewSchedule = (secMappingId, { enabled = true } = {}) =>
  useQuery({
    queryKey: ['candidate', 'interview-schedule', secMappingId],
    queryFn: () => CandidateService.getInterviewSchedule(secMappingId),
    enabled: enabled && !!secMappingId,
    refetchOnMount: 'always',
  });
